// store/authStore.ts
import { create } from 'zustand';
import { authService } from '../services/authService';
import type { UserProfile, EducationLevel } from '../types/user';

interface AuthState {
  user: UserProfile | null;
  isLoading: boolean;
  error: string | null;
  verificationId: string | null;
  needsProfileCreation: boolean;
  setUser: (user: UserProfile | null) => void;
  setVerificationId: (id: string | null) => void;
  sendOtp: (phoneNumber: string) => Promise<void>;
  verifyOtp: (code: string) => Promise<void>;
  createUserProfile: (profile: { displayName: string; email: string; currentEducation: EducationLevel }) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isLoading: false,
  error: null,
  verificationId: null,

  needsProfileCreation: false,

  setUser: (user: UserProfile | null) => {
    set({ user });
  },

  setVerificationId: (id: string | null) => {
    set({ verificationId: id });
  },

  sendOtp: async (phoneNumber: string) => {
    set({ isLoading: true, error: null, needsProfileCreation: false });
    try {
      const verificationId = await authService.sendOtp(phoneNumber);
      set({ verificationId, isLoading: false });
    } catch (err) {
      set({ 
        error: err instanceof Error ? err.message : 'Failed to send OTP', 
        isLoading: false 
      });
    }
  },

  verifyOtp: async (code: string) => {
    const { verificationId } = get();
    if (!verificationId) {
      set({ error: 'No verification ID found' });
      return;
    }
    
    set({ isLoading: true, error: null });
    try {
      const user = await authService.verifyOtp(verificationId, code);
      if (user) {
        // Check if user profile exists in Firestore
        const profile = await authService.getUserProfile(user.uid);
        if (profile) {
          set({ user: profile, needsProfileCreation: false, isLoading: false });
        } else {
          // New user, need to collect profile info
          set({ needsProfileCreation: true, isLoading: false });
        }
      }
    } catch (err) {
      set({ 
        error: err instanceof Error ? err.message : 'Invalid OTP', 
        isLoading: false 
      });
    }
  },

  createUserProfile: async (profile: { displayName: string; email: string; currentEducation: EducationLevel }) => {
    const { auth } = await import('../services/firebase');
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      set({ error: 'No authenticated user' });
      return;
    }
    
    set({ isLoading: true, error: null });
    try {
      const userProfile = await authService.createUserProfile(
        firebaseUser,
        profile
      );
      set({ user: userProfile, needsProfileCreation: false, isLoading: false });
    } catch (err) {
      set({ 
        error: err instanceof Error ? err.message : 'Failed to create profile', 
        isLoading: false 
      });
    }
  },

  logout: async () => {
    set({ isLoading: true });
    try {
      await authService.signOut();
      set({ user: null, verificationId: null, isLoading: false });
    } catch (err) {
      set({ 
        error: err instanceof Error ? err.message : 'Logout failed', 
        isLoading: false 
      });
    }
  },

  clearError: () => {
    set({ error: null });
  },
}));
